<script setup lang="ts">
import axiosInstance from '@/plugins/axios';
import { onMounted, ref, watch, inject } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToast } from 'primevue/usetoast';
import { getCombinedFaqs, strippedHtml, getFaqsByLangId, IFaqMergedCategory } from '../helper'
import { IFaq } from '@airin/shared-types';
import type { Ref } from 'vue'

const gF = inject('gF') as any;
const rootLanguageId: Ref<string> = ref(import.meta.env.VITE_ADMIN_DEFAULT_TRANSLATION_ID as string);
const route = useRoute();
const toast = useToast();
const selectedCategory = ref<IFaqMergedCategory>({ id: 'all', name: 'All' });
const router = useRouter();
const faqCategories = ref<IFaqMergedCategory[]>([{ id: "all", name: "All" }]);
const selectedFaqs = ref<Map<string, IFaq>>(new Map());

const rootLangFaqs = ref<Map<string, IFaq>>(new Map());
const targetLangFaqs = ref<Map<string, IFaq>>(new Map());
const targetLanguageCode: Ref<string> = ref(route.params.translation as string);


const getAllCategories = async () => {
    try {
        const res = await axiosInstance.get('/faq/categories/getAll');
        const convertedArray = Object.entries(res.data).map(([key, value]) => ({
            id: key,
            ...value,
        }));
        convertedArray.unshift({ id: 'all', name: 'All' });
        faqCategories.value = convertedArray;

    } catch (e) {
        console.error('Error fetching categories:', e.response?.data || e.message);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load categories: ' + (e.response?.data?.message || e.message),
            life: 3000,
        });
    }
};



const truncateText = (text: string, length: number): string => {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
};

const updateFaqSelection = async () => {
    try {
        if (!selectedCategory.value || selectedCategory.value?.id === 'all') {
            selectedFaqs.value = (await getFaqsByLangId(rootLanguageId.value)) || {};
        } else {
            const filteredFaqs: Record<string, any> = {};

            rootLangFaqs.value.forEach((values, key) => {
                if (values.category === selectedCategory.value.id) {
                    filteredFaqs[key] = {
                        question: values.question,
                        answer: values.answer,
                    };
                }
            });

            selectedFaqs.value = filteredFaqs;
        }
    } catch (error) {
        console.error('Error fetching FAQ:', error);
    }
};




const getCategoryByFaqId = (faqId: string): string => {
    let faqList = Array.isArray(rootLangFaqs.value)
        ? rootLangFaqs.value
        : Array.from(rootLangFaqs.value.values?.() || []);

    const faq = faqList.find(faq => faq._id === faqId);
    if (!faq) return '';

    const category = faqCategories.value.find(category => category.id == faq.category);
    return category ? category.name : '';
};



watch(selectedCategory, async () => {
    await updateFaqSelection();
});


onMounted(async () => {
    gF.startLoading();
    rootLangFaqs.value = await getCombinedFaqs(rootLanguageId.value);
    await updateFaqSelection();
    await getAllCategories();
    targetLangFaqs.value = await getFaqsByLangId(targetLanguageCode.value);
    gF.endLoading();
});
</script>

<template>
    <div>
        <div class="flex justify-content-between align-items-center mb-3">
            <!-- Category Selector -->
            <Select v-model="selectedCategory" class="w-50 mt-3 mb-4" fluid :options="faqCategories" optionLabel="name"
                placeholder="Category">
            </Select>

            <!-- Legende -->
            <div class="legend mb-4">
                <Tag severity="warn">Untranslated</Tag> - Text that still needs to be translated.
            </div>
        </div>

        <!-- FAQ Table -->
        <DataTable :value="selectedFaqs" tableStyle="min-width: 50rem">
            <Column field="question" header="Question">
                <template #body="{ data, index }">
                    <div v-if="targetLangFaqs[index]?.question">
                        {{ strippedHtml(targetLangFaqs[index].question) }}
                    </div>
                    <div v-else>
                        <Tag severity="warn">{{ strippedHtml(data.question) }}</Tag>
                    </div>
                </template>
            </Column>
            <Column field="answer" header="Answer">
                <template #body="{ data, index }">
                    <div v-if="targetLangFaqs[index]?.answer">
                        {{ strippedHtml(truncateText(targetLangFaqs[index].answer, 100)) }}
                    </div>
                    <div v-else>
                        <Tag severity="warn">{{ strippedHtml(truncateText(data.answer, 100)) }}</Tag>
                    </div>
                </template>
            </Column>
            <Column header="Category">
                <template #body="{ data, index }">
                    <Tag severity="info">{{ getCategoryByFaqId(index) }}</Tag>
                </template>
            </Column>
            <Column header="Actions">
                <template #body="{ data, index }">
                    <Button class="p-button-tuev" @click="router.push('qa/edit/' + index)">
                        <font-awesome-icon :icon="['fas', 'edit']" />&nbsp;Edit
                    </Button>
                </template>
            </Column>
        </DataTable>
    </div>
</template>


<style scoped>
/* Highlight untranslated text */
.highlight {
    background-color: #ffeeba;
    color: #856404;
    padding: 0.5rem;
    border-radius: 4px;
}

.p-tag {
    font-weight: normal !important;
}
</style>
